import React, { FC, useEffect, useState } from 'react';
import FormGroup from 'components/form/FormGroup';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import styled from 'styled-components';
import { FaEdit, FaEye, FaRegSave, FaFileAlt, FaFileUpload, FaInfo } from 'react-icons/fa';
import API from 'core/Api';
import Badge from 'components/Badge';
import { ISelectOptionProps } from 'components/form/types';
import { formatDate } from 'utils/helpers/formatters';
import { createErrorFromResponse } from 'core/Error';
import setServerErrors from 'utils/helpers/formErrors';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useModal } from 'store/modal';
import { IDoc } from '../types';

interface IProps {
  doc: IDoc;
  methods: any;
  documents: IDoc[];
  docHolderIsExternal: boolean;
  setMultiActions: (value: { [key: string]: any }) => void;
  isMultilingual: boolean;
  isFirstElement: boolean;
  isOneExternalDoc: boolean;
}

const FileWrapperComponent: FC<IProps> = ({
  doc,
  methods,
  documents,
  docHolderIsExternal,
  setMultiActions,
  isMultilingual,
  isFirstElement,
  isOneExternalDoc,
}: IProps) => {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const [isEditMode, setIsEditMode] = useState(false);
  const [saveClick, setSaveClick] = useState(false);

  const { type, document_id: docId, scenario, expiration_period: expPeriod } = doc;
  const { register, watch, setValue } = useFormContext();

  const expirationPeriodInput: string | undefined = watch(`doc-${docId}-expiration_period` as any);
  const docScenarioInput: string | undefined = watch(`doc-${docId}-scenario` as any);
  const docExpirationDateInput: string | undefined = watch(`doc-${docId}-expiration_date` as any);

  const isExternal = type === 'external';
  const isFirstExternalNotFirstElement = isExternal && isFirstElement && !isOneExternalDoc;

  const handleSave = () => {
    if (isExternal) {
      setSaveClick(true);
    }
    setIsEditMode(false);
  };

  useEffect(() => {
    register(`doc-${docId}-expiration_period ` as `${string}`);
    register(`doc-${docId}-scenario` as `${string}`);
    register(`doc-${docId}-expiration_date` as `${string}`);
  }, [register, docId]);

  useEffect(() => {
    const propagateChanges = () => {
      showModal({
        type: 'confirm',
        heading: t('docWizard.step5.confirmMassChangeHeading'),
        content: t('docWizard.step5.confirmMassChangeMessage'),
        onConfirm: () => {
          documents.forEach(document => {
            if (document.type === 'external' && document.document_id !== docId) {
              setValue(`doc-${document.document_id}-expiration_period` as `${string}`, expirationPeriodInput);
              setValue(`doc-${document.document_id}-scenario` as `${string}`, docScenarioInput);
              setValue(`doc-${document.document_id}-expiration_date` as `${string}`, docExpirationDateInput);
            }
          });
          hideModal();
        },
        confirmLabel: t('docWizard.step5.confirmMassChangeLabel'),
      });
    };

    if (saveClick && isFirstExternalNotFirstElement) {
      setSaveClick(false);
      propagateChanges();
    }
  }, [
    expirationPeriodInput,
    docScenarioInput,
    docExpirationDateInput,
    setValue,
    docId,
    type,
    docHolderIsExternal,
    handleSave,
  ]);

  const renderDocIcon = () => {
    if (!isExternal)
      return (
        <TooltipWrapper
          id="tooltip-internalDoc"
          overlay={t('documents.internalDoc')}
        >
          <FaFileAlt
            size={18}
            color="#32bfa3"
          />
        </TooltipWrapper>
      );
    if (isExternal)
      return (
        <TooltipWrapper
          id="tooltip-externalDoc"
          overlay={t('documents.externalDoc')}
        >
          <FaFileUpload
            size={18}
            color="#32bfa3"
          />
        </TooltipWrapper>
      );

    return null;
  };

  const docName = methods.watch(`doc-${docId}-name`);
  const docNameEn = methods.watch(`doc-${docId}-name-en`);
  const docScenario = methods.watch(`doc-${docId}-scenario`) as ISelectOptionProps;
  const docStoragePeriod = methods.watch(`doc-${docId}-expiration_period`) as ISelectOptionProps;
  const docStorageDate = methods.watch(`doc-${docId}-expiration_date`) as any;

  // useEffect(() => {
  //   methods.setValue(`doc-${docId}-expiration_date`, null);
  // }, [docStoragePeriod]);

  useEffect(() => {
    if (isExternal) {
      /**
       * If doc is external, then on choosing scenario:
       *
       * - I-1 / I-4 / I-5 - if the user creating the doc is employer/moderator
       * - II-1 - if the user creating the doc is employee
       *
       * then show the multi actions section if at least one
       * of the docs has the I-1/I-4/I-5 or II-1 scenario chosen.
       *
       * Checking whether to show multi actions
       * is in step5 `checkIfExternalDocHasMultiActions`
       *
       * TODO Scenario Info has to be added for internal documents too.
       */
      if (
        docScenario?.value === 'I-1' ||
        docScenario?.value === 'I-4' ||
        docScenario?.value === 'I-5' ||
        docScenario?.value === 'II-1'
      ) {
        setMultiActions({
          [docId]: { hasMultiActions: true },
        });
      } else {
        setMultiActions({
          [docId]: { hasMultiActions: false },
        });
      }
    }
  }, [docScenario]);

  return (
    <FileWrapper>
      <FileRow>
        <FileDetails>
          {isEditMode ? (
            <DocHolder>
              <Forty>
                {renderDocIcon()}
                <FormGroup
                  controlId="formGroupName"
                  label={t('documents.docName')}
                  type="text"
                  name={`doc-${docId}-name`}
                  isRequired
                  formGroupClassName="d-inline-block ml-1 full-wrapper"
                />
                {isMultilingual && !isExternal && (
                  <FormGroup
                    controlId="formGroupName"
                    label={t('documents.docName_lang2')}
                    type="text"
                    name={`doc-${docId}-name-en`}
                    isRequired
                    formGroupClassName="d-inline-block ml-1 full-wrapper"
                  />
                )}
              </Forty>
              {isExternal && (
                <Sixty>
                  <div className="badge-wrapper">
                    <FormGroup
                      controlId="formGroupStoragePeriod"
                      label={t('docWizard.step5.storagePeriod')}
                      type="select"
                      as="select"
                      name={`doc-${docId}-expiration_period`}
                      isRequired
                      isSearchable
                      selectOptions={expPeriod}
                      formGroupClassName="d-inline-block ml-1 wrapper-100"
                    />
                    <TooltipWrapper
                      id="tooltip-info"
                      overlay={t(`documentScenarioInfo.${docScenario.value}`)}
                      wrapperClass="wrapper-100"
                    >
                      <FormGroup
                        controlId="formGroupType"
                        label={t('docWizard.step5.docScenario')}
                        type="select"
                        as="select"
                        name={`doc-${docId}-scenario`}
                        isRequired
                        isSearchable
                        selectOptions={scenario}
                        formGroupClassName="d-inline-block ml-1 wrapper-100"
                      />
                    </TooltipWrapper>
                  </div>
                </Sixty>
              )}
            </DocHolder>
          ) : (
            <DocHolder>
              <Forty>
                {renderDocIcon()}
                <DocTitle className="mb-0 ml-2 d-inline-block">
                  {docName}
                  {isMultilingual && !isExternal ? ` / ${docNameEn}` : null}
                </DocTitle>
              </Forty>
              {isExternal && (
                <Sixty>
                  <div className="badge-wrapper">
                    <span className="ml-2">
                      <Badge
                        label={`${t('docWizard.step5.storagePeriod')} ${docStoragePeriod?.label} ${
                          docStorageDate ? formatDate(docStorageDate, 'dd.mm.YYY') : ''
                        }`}
                        variant="info"
                      />
                    </span>
                    <span className="ml-2">
                      <Badge
                        label={docScenario?.label}
                        variant="info"
                      />
                    </span>
                  </div>
                </Sixty>
              )}
            </DocHolder>
          )}
        </FileDetails>
        <ButtonsWrapper>
          {isExternal && (
            <span className="mr-0 mr-md-2">
              <TooltipWrapper
                id="tooltip-info"
                overlay={t(`documentScenarioInfo.${docScenario?.value}`)}
              >
                <Button
                  btnType="secondary"
                  size="sm"
                  outlined
                  onClick={event => event.preventDefault()}
                >
                  <FaInfo size={14} />
                </Button>
              </TooltipWrapper>
            </span>
          )}
          {isEditMode && (
            <span>
              <TooltipWrapper
                id="tooltip-save"
                overlay={isFirstExternalNotFirstElement ? t('docWizard.step5.saveWithOption') : t('common.save')}
              >
                <Button
                  btnType="info"
                  size="sm"
                  outlined
                  onClick={handleSave}
                >
                  <FaRegSave size={14} />
                </Button>
              </TooltipWrapper>
            </span>
          )}
          {!isEditMode && (
            <TooltipWrapper
              id="tooltip-edit"
              overlay={isFirstExternalNotFirstElement ? t('docWizard.step5.editWithOption') : t('common.edit')}
            >
              <Button
                btnType="primary"
                size="sm"
                outlined
                onClick={() => setIsEditMode(!isEditMode)}
              >
                <FaEdit size={14} />
              </Button>
            </TooltipWrapper>
          )}
          <span className="ml-2">
            <TooltipWrapper
              id="tooltip-preview"
              overlay={t('common.preview')}
            >
              <Button
                btnType="secondary"
                size="sm"
                outlined
                onClick={() => {
                  API.get(`documents/${docId}/download`)
                    .then(res => {
                      window.open(res.data, '_blank');
                    })
                    .catch(err => setServerErrors(createErrorFromResponse(err).errors, methods.setError));
                }}
              >
                <FaEye size={14} />
              </Button>
            </TooltipWrapper>
          </span>
        </ButtonsWrapper>
      </FileRow>
      <FileRow>
        {isEditMode && (
          <FileDetails>
            <Sixty>
              <StorageperiodContainer>
                {docStoragePeriod?.value === 0 && (
                  <FormGroup
                    controlId="formGroupStorageDate"
                    label={t('docWizard.step5.storageDate')}
                    type="datepicker"
                    name={`doc-${docId}-expiration_date`}
                    dateFormat="dd.MM.yyyy"
                    minDate={new Date()}
                    isRequired
                    formGroupClassName="d-inline-block ml-1 wrapper-100"
                    showMonthDropdown
                    showYearDropdown
                  />
                )}
              </StorageperiodContainer>
            </Sixty>
          </FileDetails>
        )}
      </FileRow>
    </FileWrapper>
  );
};

const FileWrapper = styled.div`
  padding: 1rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const FileRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FileDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 85%;
`;

const StorageperiodContainer = styled.div`
  margin-left: 17px;
  width: 65.5%;

  @media ${props => props.theme.breakpoints.sm} {
    margin-left: 0;
    width: 100%;
  }
`;

const Forty = styled.div`
  width: 40%;
  display: flex;
  align-items: center;

  .full-wrapper {
    width: 100%;
  }

  @media ${props => props.theme.breakpoints.sm} {
    width: 100%;
  }
`;

const Sixty = styled.div`
  width: 60%;
  display: flex;
  align-items: center;

  .badge-wrapper {
    width: 100%;
    display: flex;
  }

  .wrapper-40 {
    width: 40%;
  }

  .wrapper-60 {
    width: 60%;
  }

  .wrapper-100 {
    width: 100%;
  }

  @media ${props => props.theme.breakpoints.sm} {
    width: 100%;

    .wrapper-40 {
      width: 100%;
    }

    .wrapper-60 {
      width: 100%;
    }

    .badge-wrapper {
      margin-top: 0.5rem;
      display: flex;
      flex-direction: column;
    }
    .ml-2 {
      margin-left: 0 !important;
    }
  }
`;

const DocTitle = styled.h5`
  width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DocHolder = styled.div`
  display: flex;
  width: 100%;

  @media ${props => props.theme.breakpoints.sm} {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
`;

const ButtonsWrapper = styled.div`
  @media ${props => props.theme.breakpoints.sm} {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-end;

    .ml-2 {
      margin-left: 0 !important;
    }
  }
`;

export default FileWrapperComponent;
